/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, {
  // useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getIsoToUsShortDateTime } from '@/utils/isoToUsShortDateTime';
import { getHighlightParts } from '@/ux-common/src/utils/getHighlightParts';
import { NormalizedMessage } from 'types/prompt-sequence-view';

import {
  alwaysRenderStringFormatToUI,
  handlePromptDataVarient,
} from '@/lib/helper';
import { useSplitPromptSequence } from '@/hooks/useSplitPromptSequence';

import { PromptSequenceIcons } from '../icons/icons';

interface CustomPromptComponentProps {
  data: NormalizedMessage | string;
  index: number;
  onSplit?: () => void;
  status: string;
  time: string;
  totalTokens?: number;
  completionTokens?: number;
  promptTokens?: number;
  span_id: string;
  type: 'input' | 'output';
  searchTerm?: string;
  globalMatchOffset?: number;
  currentMatchIndex?: number;
  matchCounter: number;
}

export const CustomPromptComponent: React.FC<CustomPromptComponentProps> = ({
  data,
  index,
  onSplit,
  status,
  time,
  totalTokens,
  completionTokens,
  promptTokens,
  span_id,
  type,
  searchTerm = '',
  globalMatchOffset,
  currentMatchIndex,
  matchCounter,
}) => {
  // const matchRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const [isClamped, setIsClamped] = useState<boolean>(false);

  const formattedDateTime = getIsoToUsShortDateTime(time);

  const paraRef = useRef<HTMLParagraphElement>(null);

  const { setSplitPayload, splitPayload } = useSplitPromptSequence();

  const isHighlighted =
    splitPayload !== null &&
    splitPayload.splitFromIndex === index &&
    splitPayload.span_id === span_id;

  // Check if the data is a type of string.
  const isString = typeof data === 'string';

  // Check if the data is a type of object.
  const entries = !isString ? Object.entries(data) : [];

  // Check if the data is an empty
  const isEmpty = !isString && entries.length === 0;

  // If the data is string type, add 'output' as role. Otherwise get the role from the existing data.
  const role = isString ? 'output' : data.role;

  // If the data is string type ... render it on to the UI. Otherwise get the "content" from the existing data object.
  const content = isString ? data : data.content;

  // Handle the display response.
  const displayResponse = handlePromptDataVarient(content);

  // This helper function from your lib ensures we always have a string to work with
  const textToRender = alwaysRenderStringFormatToUI(displayResponse) || '';

  // Check if this prompt has matches
  const hasMatches = useMemo(() => {
    if (!searchTerm.trim()) return true; // Show all when no search term
    return getHighlightParts(textToRender, searchTerm).some(
      (part) => part.isMatch
    );
  }, [textToRender, searchTerm]);

  // Check if the prompt is clamped to 2 lines or not.
  useLayoutEffect(() => {
    function checkClamp() {
      // Get the p element.
      const el = paraRef.current;

      // Early return if we don't have p element
      if (!el) return;

      // Check if the prompt is more than in two lines, if so then show a button
      setIsClamped(el.scrollHeight > el.clientHeight);
    }
    // Initial check
    checkClamp();

    // Check if the screen resizes.
    window.addEventListener('resize', checkClamp);

    // Remove event listener.
    return () => {
      window.removeEventListener('resize', checkClamp);
    };
  }, [displayResponse]);

  // useEffect(() => {
  //   if (
  //     currentMatchIndex !== undefined &&
  //     matchRefs.current[currentMatchIndex]
  //   ) {
  //     matchRefs.current[currentMatchIndex]?.scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'center',
  //     });
  //   }
  // }, [currentMatchIndex]);

  // Early return if no matches when searching
  if (!hasMatches) {
    return null;
  }

  //  Early return for the empty data.
  if (isEmpty) {
    return null;
  }

  // Create a separate ROLE component that can be reused in 2 places within this component.
  const PromptRole = () => {
    return (
      <p
        className={`text-body-sm leading-4 tracking-[0%] ${
          role === 'human' ? 'text-[#768FCC]' : 'text-[#8C9EA1]'
        }`}
      >
        {role === 'output' ? '' : role.length > 1 ? role : ''}
      </p>
    );
  };

  // On click on to the split button pass the required data to the context.
  function handleSplitClick() {
    setSplitPayload({
      longPrompt: displayResponse,
      splitFromIndex: index!,
      span_id,
      role: <PromptRole />,
      globalMatchOffset: globalMatchOffset!,
    });
    onSplit?.();
  }

  // Pick the circle background.
  const circleBgClass =
    type === 'input'
      ? index === 0
        ? 'bg-[#1E2A4A]'
        : 'bg-transparent'
      : status === 'error'
        ? 'bg-notification-error-dark'
        : 'bg-[#34BCAF1F]';

  return (
    <div className="relative w-full">
      <div className="px-4">
        <div className="flex gap-2">
          {/* Circles */}
          <div
            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${circleBgClass}`}
          >
            {type === 'input'
              ? index === 0 && PromptSequenceIcons.HumanIcon('#4F71C4')
              : status === 'error'
                ? PromptSequenceIcons.ErrorStatusIcon('text-white')
                : PromptSequenceIcons.SystemIcon('#20ACA1')}
          </div>

          {/* Prompt */}
          <div
            className={`relative rounded-bl-lg rounded-br-lg rounded-tr-lg bg-[#0A2D3A] px-4 py-[10px]`}
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <div>
                <PromptRole />
                <div className="group flex items-start">
                  <p
                    ref={paraRef}
                    className="mt-0.5 line-clamp-2 break-words break-all text-[14px] font-normal leading-[140%] tracking-[0%] text-[#EBEFF1]"
                  >
                    {getHighlightParts(textToRender, searchTerm).map(
                      (part, i) => {
                        if (part.isMatch) {
                          // Calculate the true global index of this specific match
                          const globalIndexOfThisMatch = matchCounter;

                          matchCounter++;

                          return (
                            <span
                              key={i}
                              // ref={(el) => {
                              //   matchRefs.current[globalIndexOfThisMatch] = el;
                              // }}
                              className={`rounded px-px ${
                                globalIndexOfThisMatch === currentMatchIndex
                                  ? 'bg-mustard-600' // Active style
                                  : 'bg-mustard-300 text-black' // Inactive style
                              }`}
                            >
                              {part.text}
                            </span>
                          );
                        }
                        return <span key={i}>{part.text}</span>;
                      }
                    )}
                  </p>
                  {isClamped && (
                    <div
                      className={`absolute right-0 top-0 flex h-full w-40 items-center justify-end rounded-br-lg rounded-tr-lg  bg-gradient-to-r from-[#0A2D3A00] to-[#0A2D3A] opacity-0 transition-opacity duration-200 group-hover:opacity-100 ${role === 'output' ? 'from-[#16595600] to-[#165956]' : ''}`}
                    >
                      <button
                        onClick={handleSplitClick}
                        className="pr-3 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                      >
                        {PromptSequenceIcons.SplitIcon(
                          `${role === 'output' ? 'text-jade' : 'text-ocean'}`
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Evals + time + tokens */}
              <div className="mt-2 flex w-full items-center justify-between">
                {/* Time and token goes here */}
                <div
                  className={`ml-auto self-end text-body-sm tracking-[2%] text-body-subtle ${
                    role === 'human' ? 'text-white/55' : 'text-body-subtle'
                  } flex items-center gap-[10px]`}
                >
                  <span>
                    {role === 'output' && totalTokens !== undefined && (
                      <>
                        <span>{`Token: ${totalTokens}`}</span>
                        {(completionTokens !== undefined ||
                          promptTokens !== undefined) && (
                          <span>
                            {` - `}
                            {completionTokens !== undefined &&
                              `${completionTokens} Completion`}
                            {completionTokens !== undefined &&
                              promptTokens !== undefined &&
                              `,  `}
                            {promptTokens !== undefined &&
                              `${promptTokens} Prompts`}
                          </span>
                        )}
                      </>
                    )}
                  </span>
                  <span>{formattedDateTime}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. The overlay, rendered on top when highlighted */}
      {isHighlighted && (
        <div className="absolute -inset-y-1.5 inset-x-0 border-l border-[#8C9EA1] bg-[#FFFFFF14]"></div>
      )}
    </div>
  );
};
